import Loading from '@/components/Loading/Loading';
import SendInputFailures from '@/components/Agents/SendValidator/Report/SendInputFailures';
import SendRunScope from '@/components/Agents/SendValidator/Report/SendRunScope';
import { SEND_UNREADABLE_TITLE } from '@/components/Agents/SendValidator/utils/SendInputFailureText';
import { useSendSession } from '@/components/Agents/SendValidator/useSendSession';
import Select from '@/components/UI/Select/Select';
import {
  sendDatasetLabel,
  sendDomainLabel,
  SUPPLEMENTAL_MODEL,
  supplementalDatasetOf,
  supplementalLabel,
} from '@/components/Agents/SendValidator/utils/SendDomains';
import {
  domainColumnEvidenceFor,
  isUndetermined,
  mixedDomainRefusalOf,
  rowCapRefusalOf,
  type SendDomainOption,
  sendDomainOptionGroupsFor,
  singleInstanceChoices,
  singleInstanceRefusalOf,
  takenByOtherFiles,
} from '@/components/Agents/SendValidator/utils/SendDomainPicker';
import SendCoercionWarnings, { SEND_COERCION_REMEDY } from '@/components/Agents/SendValidator/SendCoercionWarnings';
import { detectionCoercionFilesOf } from '@/components/Agents/SendValidator/utils/SendCoercion';
import { _isNil, _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';
import type { AgentSessionApiId } from '@/model/Agent.model';
import { ApiService, ResponseType, type ApiErrorResponse } from '@/support/ApiService';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { isValidationErrorBody } from '@/utils/apiErrorResponse';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { type FC, type PropsWithChildren, useMemo, useState } from 'react';
import styles from './AgentSendConfirm.module.scss';

interface AgentSendConfirmProps {
  /** The SEND session whose detected bundle to confirm. */
  sessionApiId: AgentSessionApiId;
  /** Injected by the modal system; closes the modal. */
  closeModal: () => void;
  /**
   * Fired once the confirm has succeeded (the validate execution is now queued), before the modal
   * closes. Lets the caller refresh the session tree so the root "Validating" node re-spins its
   * loader immediately, signalling the newly-queued run.
   */
  onConfirmed?: () => void;
}

type ConfirmError = ApiErrorResponse<'POST /api/v1/ai-agents/send-validator/{sessionApiId}/confirm-domain'>['error'];

/**
 * The server's own reason for refusing a confirmation, such as a study out of scope or a detection
 * stored before scope was read, which it gives against the session; otherwise its first message.
 */
const refusalOf = (error: ConfirmError | undefined): string | undefined => {
  const data = error?.response?.data;
  const errors = isValidationErrorBody(data) ? data.errors : undefined;
  return errors?.session_api_id?.[0] ?? Object.values(errors ?? {}).flat()[0];
};

/** A row's warning that blocks confirmation until the user acts on it. */
const RowAlert: FC<PropsWithChildren<{ testElement: string }>> = ({ testElement, children }) => (
  <p className={`f6 near-black ma0 pa2 bl bw2 b--orange ${styles.alert}`} role="alert" data-test-element={testElement}>
    {children}
  </p>
);

/**
 * Modal that confirms (or overrides) the per-file SENDIG domain a SEND bundle was detected as, before
 * validation runs. The `send_domain_confirm` tree node supplies only the session id; the detections
 * live on the detect execution, so the modal fetches the team-scoped domain-confirmation view. Rows
 * are labelled by the detection's `original_filename`, as the uploader is gone by the time this opens.
 *
 * Once `confirmed_domains` is present the panel reopens read-only, showing the ACTUAL confirmed
 * domains with no submit: the flow is strictly linear (detect → confirm → results) and must not be
 * re-run.
 *
 * It also fetches the session, because a run stopped on an unreadable file has nothing to confirm:
 * an input-failure result is listed with only a Close action, never the domain form or its empty
 * state, so an unreadable upload cannot look like a bundle with nothing detected. The load error shows
 * when the confirmation view fails, or when the session fails and nothing was detected.
 *
 * Files whose columns look rewritten by Excel are flagged above the rows, with what to do about it,
 * before the user commits to a validation run on them. The warning never blocks confirmation.
 *
 * A session holding a detection produced by an earlier version has nothing it can confirm either.
 * The server says so (`detection_status`), and the modal asks for the files to be uploaded again
 * rather than claiming nothing was detected.
 *
 * A supplemental qualifier file is offered as the dataset its own RDOMAIN makes it (`SUPPMI`), and
 * confirmed as that dataset. A file detected as a custom domain is flagged on its row and the bundle
 * cannot be confirmed: this validator can't check custom domains, and the server refuses them.
 *
 * Detection is the validator's alone. Each picker offers the ranked candidates first, then every domain
 * for an override. A file detection could not tell is offered with nothing chosen and says so, and the
 * bundle can't be confirmed until it is chosen. Where the file's own DOMAIN column agrees, that is shown
 * as evidence. What the server would refuse is said on the row before submitting, and blocks it: a file
 * whose DOMAIN column names more than one domain, a file with more rows than the chosen domain holds,
 * and a second file chosen as a dataset a study has only one of, which the other rows' pickers also
 * stop offering.
 */
const AgentSendConfirm: FC<AgentSendConfirmProps> = ({ sessionApiId, closeModal, onConfirmed }) => {
  const { response, loading } = useApiHook({
    endpoint: 'GET /api/v1/ai-agents/send-validator/{sessionApiId}/domain-confirmation',
    path: { sessionApiId },
    options: {
      onError: {
        throw: false,
        preserveOriginalError: false,
        toast: false,
        capture: true,
        slug: 'agent-send-confirm-load',
      },
    },
  });

  const {
    sessionResult,
    loading: sessionLoading,
    failed: sessionFailed,
  } = useSendSession(sessionApiId, 'agent-send-confirm-session-load');
  const inputFailure = sessionResult.kind === 'input_failure' ? sessionResult.result : null;

  const confirmation = useMemo(() => (response?.type === 'success' ? response.body : null), [response]);
  const detections = useMemo(() => confirmation?.detections ?? [], [confirmation]);
  const isEarlierVersion = confirmation?.detection_status === 'earlier_version';
  // The server refuses to confirm a study out of scope, so the step says so before anyone tries.
  const outOfScope = confirmation?.scope?.state === 'out_of_scope';

  // A non-empty map flips the panel to its read-only, non-resubmittable mode.
  const confirmedDomains = useMemo(
    () =>
      (confirmation?.confirmed_domains ?? []).reduce<Record<string, string>>((acc, confirmed) => {
        acc[confirmed.attachment_api_id] = confirmed.domain_code;
        return acc;
      }, {}),
    [confirmation]
  );
  const confirmedDatasets = useMemo(
    () =>
      (confirmation?.confirmed_domains ?? []).reduce<Record<string, string>>((acc, confirmed) => {
        if (_notNil(confirmed.dataset_name)) {
          acc[confirmed.attachment_api_id] = confirmed.dataset_name;
        }
        return acc;
      }, {}),
    [confirmation]
  );
  const isConfirmed = _isNotEmpty(confirmedDomains);
  const holdsCustomDomain = detections.some((file) => _notNil(file.custom_domain));

  // Per-file chosen domain code, keyed by attachment id, seeded lazily from the detected domain.
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const singleInstanceDatasets = useMemo(() => confirmation?.single_instance_datasets ?? [], [confirmation]);

  /** The domain a file is set to: confirmed, chosen, or detected; undefined when nothing is chosen yet. */
  const domainFor = (attachmentApiId: string, detectedDomain: string): string | undefined =>
    confirmedDomains[attachmentApiId] ??
    choices[attachmentApiId] ??
    (_isNotBlank(detectedDomain) ? detectedDomain : undefined);

  const singleInstanceChosen = singleInstanceChoices(
    detections.map((file) => ({
      attachmentApiId: file.attachment_api_id,
      filename: file.original_filename,
      chosen: domainFor(file.attachment_api_id, file.detected_domain),
    })),
    singleInstanceDatasets
  );
  const blocked =
    !isConfirmed &&
    ([...singleInstanceChosen.values()].some((files) => files.length > 1) ||
      detections.some((file) => {
        const chosen = domainFor(file.attachment_api_id, file.detected_domain);
        return _isNil(chosen) || _notNil(mixedDomainRefusalOf(file)) || _notNil(rowCapRefusalOf(file, chosen));
      }));

  const handleConfirm = async (): Promise<void> => {
    if (!_isNotEmpty(detections) || outOfScope || holdsCustomDomain || blocked) {
      return;
    }
    setConfirmError(null);
    setSubmitting(true);
    const files = detections.map((file) => {
      const domainCode = domainFor(file.attachment_api_id, file.detected_domain) ?? '';
      return {
        attachment_api_id: file.attachment_api_id,
        domain_code: domainCode,
        dataset_name: domainCode === SUPPLEMENTAL_MODEL ? supplementalDatasetOf(file) : undefined,
      };
    });
    const confirmResponse = await ApiService.call({
      endpoint: 'POST /api/v1/ai-agents/send-validator/{sessionApiId}/confirm-domain',
      path: { sessionApiId },
      body: { files },
      options: {
        onError: { toast: false, throw: false, capture: true, slug: 'send-validator-confirm-domain' },
      },
    });
    setSubmitting(false);
    if (confirmResponse.type === ResponseType.Success && _notNil(confirmResponse.body)) {
      onConfirmed?.();
      closeModal();
      return;
    }
    setConfirmError(refusalOf(confirmResponse.error) ?? 'Failed to confirm domains.');
  };

  if (loading || sessionLoading) {
    return <Loading txt="Loading detected domains" />;
  }

  if (_notNil(inputFailure)) {
    return (
      <ModalContainer size="small" testKey="agent-send-confirm-modal">
        <ModalHeader title={SEND_UNREADABLE_TITLE} closeModal={closeModal} className="pa3 bb b--moon-gray bg-white" />
        <div className={styles.body} data-testid="agent-send-confirm-content">
          <SendInputFailures failures={inputFailure.failures} testId="agent-send-confirm__input-failures" />
        </div>
        <div className="pa3 bt b--moon-gray">
          <ModalActions cancelBtnText="Close" onCancel={closeModal} />
        </div>
      </ModalContainer>
    );
  }

  return (
    <ModalContainer size="small" testKey="agent-send-confirm-modal">
      <ModalHeader title="Confirm SEND domains" closeModal={closeModal} className="pa3 bb b--moon-gray bg-white" />
      <div className={styles.body} data-testid="agent-send-confirm-content">
        {response?.type === 'error' || (sessionFailed && !_isNotEmpty(detections)) ? (
          <p className="gray tc" data-test-element="error">
            Couldn't load the detected domains.
          </p>
        ) : _isNotEmpty(detections) ? (
          <div className="flex flex-column" data-test-element="detections">
            <p className={`f6 ma0 mb3 ${isConfirmed ? styles.confirmedNote : 'dark-gray'}`}>
              {isConfirmed
                ? 'These domains have been confirmed for this session and can no longer be changed.'
                : outOfScope
                  ? "These are the detected domains, but they can't be confirmed for a study the validator can't check."
                  : 'Confirm the detected SENDIG domain for each file, or override it before validation runs.'}
            </p>
            <SendRunScope
              scope={confirmation?.scope}
              trcProxies={confirmation?.trc_proxies}
              stage="confirm"
              testId="agent-send-confirm__scope"
            />
            <SendCoercionWarnings
              files={detectionCoercionFilesOf(detections)}
              remedy={SEND_COERCION_REMEDY}
              testId="agent-send-confirm__coercion-warnings"
            />
            <ul className={styles.rows}>
              {detections.map((file) => {
                const selected = domainFor(file.attachment_api_id, file.detected_domain);
                const supplementalDataset = supplementalDatasetOf(file);
                const selectedLabel =
                  selected === SUPPLEMENTAL_MODEL
                    ? supplementalLabel(supplementalDataset)
                    : sendDomainLabel(selected ?? '');
                const evidence = domainColumnEvidenceFor(file, selected);
                const mixed = mixedDomainRefusalOf(file);
                const overRowCap = rowCapRefusalOf(file, selected);
                const sharing = _notNil(selected) ? (singleInstanceChosen.get(selected) ?? []) : [];
                const collision =
                  _notNil(selected) && sharing.length > 1
                    ? singleInstanceRefusalOf(
                        selected,
                        sharing.map((other) => other.filename)
                      )
                    : undefined;
                const takenElsewhere = takenByOtherFiles(singleInstanceChosen, file.attachment_api_id);
                const ranked = sendDomainOptionGroupsFor(file, supplementalDataset);
                return (
                  <li key={file.attachment_api_id} className={styles.row} data-test-element="detection-row">
                    <span className={styles.filename} data-test-element="detection-filename">
                      {file.original_filename}
                    </span>
                    {_notNil(file.custom_domain) && (
                      <RowAlert testElement="custom-domain">
                        {file.custom_domain} looks like a custom domain: its DOMAIN column names a code SENDIG
                        doesn&apos;t define. This validator can&apos;t check custom domains against SENDIG. Remove{' '}
                        {file.original_filename} and upload the other files again to validate them.
                      </RowAlert>
                    )}
                    {isConfirmed ? (
                      <span className={styles.confirmedDomain} data-test-element="detection-confirmed-domain">
                        {_notNil(confirmedDatasets[file.attachment_api_id])
                          ? sendDatasetLabel(confirmedDatasets[file.attachment_api_id])
                          : sendDomainLabel(selected ?? '')}
                      </span>
                    ) : (
                      <div className={styles.picker}>
                        {isUndetermined(file) &&
                          _isNil(file.custom_domain) &&
                          _isNil(choices[file.attachment_api_id]) && (
                            <p className="f6 near-black ma0" role="status" data-test-element="undetermined">
                              We couldn&apos;t tell which domain this is.{' '}
                              {ranked.length > 1 ? 'Pick one: the closest matches are listed first.' : 'Pick one.'}
                            </p>
                          )}
                        <Select<SendDomainOption>
                          isMulti={false}
                          className="w-100"
                          options={ranked}
                          value={_notNil(selected) ? { label: selectedLabel, value: selected } : null}
                          isOptionDisabled={(option) => takenElsewhere.has(option.value)}
                          aria-label={`Domain for ${file.original_filename}`}
                          placeholder="Pick a domain…"
                          isSearchable
                          onChange={(option) => {
                            if (_notNil(option)) {
                              setChoices((prev) => ({ ...prev, [file.attachment_api_id]: option.value }));
                            }
                          }}
                        />
                        {_notNil(evidence) && (
                          <p className="f6 dark-gray ma0" data-test-element="domain-evidence">
                            {evidence}
                          </p>
                        )}
                      </div>
                    )}
                    {!isConfirmed && _notNil(mixed) && <RowAlert testElement="mixed-domain">{mixed}</RowAlert>}
                    {!isConfirmed && _notNil(overRowCap) && (
                      <RowAlert testElement="over-row-cap">{overRowCap}</RowAlert>
                    )}
                    {!isConfirmed && _notNil(collision) && (
                      <RowAlert testElement="single-instance">{collision}</RowAlert>
                    )}
                  </li>
                );
              })}
            </ul>
            {_notNil(confirmError) && (
              <div className="red f6 mt2" data-test-element="confirm-error">
                {confirmError}
              </div>
            )}
          </div>
        ) : isEarlierVersion ? (
          <p className="gray tc" data-test-element="earlier-version">
            {isConfirmed
              ? 'This upload was detected by an earlier version, so its confirmed domains cannot be shown.'
              : 'This upload was detected by an earlier version. Re-upload to continue.'}
          </p>
        ) : (
          <p className="gray tc" data-test-element="empty-state">
            No detected domains to confirm.
          </p>
        )}
      </div>
      <div className="pa3 bt b--moon-gray">
        {isConfirmed || isEarlierVersion ? (
          <ModalActions cancelBtnText="Close" onCancel={closeModal} />
        ) : (
          <ModalActions
            submitBtnText="Confirm domains"
            onSubmit={_isNotEmpty(detections) ? handleConfirm : undefined}
            submitButtonProps={{
              disabled: submitting || !_isNotEmpty(detections) || outOfScope || holdsCustomDomain || blocked,
            }}
            cancelBtnText="Close"
            onCancel={closeModal}
          />
        )}
      </div>
    </ModalContainer>
  );
};

export default AgentSendConfirm;
